<template>
    <div
        :id="`quizId=${quiz.id}`"
        v-if="lesson && quiz"
        @wheel="handleWheel($event)"
        @touchmove="handleWheel($event)"
        class="practice-view"
    >
        <transition name="fade">
            <div v-if="displayModal" class="practice-view-modal">
                <span @click="displayModal = false" class="practice-view-modal__overflow"></span>
                <PracticeMenu :quizzes="quizzes" :lesson="lesson" />
            </div>
        </transition>

        <component v-if="_practice && _practice.info_unit" :key="`practice-styles`" is="style">
            {{ getStyles(quiz, _practice.info_unit) }}
        </component>

        <FileTasks />

        <transition name="fade" mode="out-in">
            <LoaderBlock key="loader" :green-color="true" v-if="loading" />
            <div key="content" class="practice-view__content" v-else>
                <sticky-page-container
                    v-if="$screenWidth > 960"
                    :style-content="contentStyle"
                    :style-aside="{ width: contentPartScreen ? proportionStyle && proportionStyle.left : null }"
                >
                    <template #aside-left>
                        <PracticeMenu :quizzes="quizzes" :lesson="lesson" />
                    </template>
                    <template #content>
                        <PracticeContent
                            v-if="quiz"
                            :data-practice-unit-id="_practice && _practice.info_unit && _practice.info_unit.id"
                            :quiz="quiz"
                            :lesson="lesson"
                            @content:check="
                                contentPartScreen = $event
                                $forceUpdate()
                            "
                        />
                    </template>
                </sticky-page-container>
                <PracticeContent
                    :data-practice-unit-id="_practice && _practice.info_unit && _practice.info_unit.id"
                    v-else-if="quiz"
                    :quiz="quiz"
                    :lesson="lesson"
                    @content:check="
                        contentPartScreen = $event
                        $forceUpdate()
                    "
                />
            </div>
        </transition>
    </div>
</template>

<script>
import FileTasks from "@components/Files/FileTasks.vue"
import { ExternalLoader } from "@helpers"
import { EventBus } from "~events"
import BugIcon from "@icons/BugIcon.vue"
import LoaderBlock from "@components/Loaders/LoaderBlock.vue"
import StickyPageContainer from "@components/Containers/StickyPageContainer.vue"
import PracticeMenu from "./components/PracticeMenu/PracticeMenu.vue"
import PracticeContent from "./components/PracticeContent/PracticeContent.vue"
import PracticeRepository from "@components/Practice/shared/repository/practice-repository"

export default {
    components: { FileTasks, StickyPageContainer, PracticeContent, PracticeMenu, LoaderBlock },
    props: {
        _practice: {
            type: Object,
            default: () => {}
        },
        _customStyle: {
            type: Object,
            default: null
        },
        _isAuth: {
            type: Boolean,
            default: false
        }
    },
    name: "PracticeView",
    data() {
        return {
            displayModal: false,
            contentPartScreen: false,
            isAuth: this._isAuth,
            lesson: null,
            loading: false,
            quiz: null,
            error: null,
            quizzes: []
        }
    },
    async created() {
        EventBus.$on("practice:display", val => {
            this.displayModal = val
        })

        const url = new URL(window.location)
        const quiz_id = parseInt(url.pathname.split("/").pop())
        const lesson_id = this._practice.lesson.id

        const { data: lesson } = await PracticeRepository.lesson({ lesson_id }).catch(e => {
            this.error = e
        })

        this.lesson = lesson

        const result = await PracticeRepository.quizzes({ lesson_id }).catch(e => {
            this.error = e
        })

        const quizzes = result ? result.data : []

        this.quizzes = quizzes

        let quiz = quizzes.find(q => q.id === quiz_id)

        if (!quiz) {
            quiz = quizzes[0]
        }

        const { data: questions } = await PracticeRepository.questions({
            lesson_id,
            quiz_id: quiz.id,
            attempt_id: quiz?.attempt?.id || null
        }).catch(e => {
            this.error = e
        })

        quiz.questions = questions

        this.quiz = quiz
    },
    mounted() {
        ExternalLoader.STYLE(["/build/css/quill.core.css", "/build/css/quill.snow.css"])
    },
    methods: {
        getStyles(quiz, unit) {
            if (!quiz?.style?.layout) {
                console.error(quiz.style)
                return
            }

            let style = quiz.style.layout

            /* if (unit.styles) {
                style = {
                    ...style,
                    ...unit.styles
                }
            }*/

            // eslint-disable-next-line no-unused-vars
            const { fontAnswer, fontQuestion, fontQuizNumeration, fontStatement } = style

            let str = `
                #quizId\\=${quiz.id} {
                    color: 'red';
                }

                #quizId\\=${quiz.id} .practice-question-description .text-view {
                    color: ${fontQuestion.color};
                    font-size: ${fontQuestion.size}px;
                    font-family: ${fontQuestion.family};
                    font-weight: ${fontQuestion.weight};
                    line-height: ${fontQuestion.height}%;
                }

                #quizId\\=${quiz.id} .number {
                    color: ${fontQuizNumeration.color};
                    font-size: ${fontQuizNumeration.size}px;
                    font-family: ${fontQuizNumeration.family};
                    font-weight: ${fontQuizNumeration.weight};
                    line-height: ${fontQuizNumeration.height}%;
                }

                #quizId\\=${quiz.id} .practice-question-description .text-view p {
                    line-height: inherit;
                }

                #quizId\\=${quiz.id} .practice-question-description .text-view p:last-child {
                    margin-bottom: 0;
                }

                #quizId\\=${quiz.id} .default-dropdown__icon path {
                    stroke: ${fontAnswer.color};
                }

                #quizId\\=${quiz.id} .practice-question__answer,
                #quizId\\=${quiz.id} .practice-question__answers .text-view,
                #quizId\\=${quiz.id} .order-answer,
                #quizId\\=${quiz.id} .default-dropdown__list-item,
                #quizId\\=${quiz.id} .list-complete-item,
                #quizId\\=${quiz.id} .order-statement-input .default-input__input,
                #quizId\\=${quiz.id} .order-statement-input .default-input__input::placeholder,
                #quizId\\=${quiz.id} .default-dropdown__current-container,
                #quizId\\=${quiz.id} .practice-question__text-area,
                #quizId\\=${quiz.id} .fill-gaps-dropdown .handler,
                #quizId\\=${quiz.id} .fill-gaps-input,
                #quizId\\=${quiz.id} .practice-question-word-from-answer .shadow,
                #quizId\\=${quiz.id} .fill-gaps-dropdown-case,
                #quizId\\=${quiz.id} .default-dropdown__placeholder,
                #quizId\\=${quiz.id} .correct-status-description .text-view {
                    color: ${fontAnswer.color};
                    font-size: ${fontAnswer.size}px;
                    font-family: ${fontAnswer.family};
                    font-weight: ${fontAnswer.weight};
                    line-height: ${fontAnswer.height}%;
                }

                #quizId\\=${quiz.id} .order-statement-input .default-input__input::placeholder,
                #quizId\\=${quiz.id} .default-dropdown__placeholder {
                    opacity: 0.5;
                }

                 #quizId\\=${quiz.id} .order-statement-description .text-view {
                    color: ${fontStatement.color};
                    font-size: ${fontStatement.size}px;
                    font-family: ${fontStatement.family};
                    font-weight: ${fontStatement.weight};
                    line-height: ${fontStatement.height}%;
                }

                #quizId\\=${quiz.id} .practice-question__answer .text-view {
                    color: inherit;
                    font-size: inherit;
                    font-family: inherit;
                    font-weight: inherit;
                    line-height: inherit;
                }

                #quizId\\=${quiz.id} .practice-question__answer .text-view p {
                    line-height: inherit;
                }

                #quizId\\=${quiz.id} .paste-word-container .default-input__input {
                    background-color: #fff;
                }

            `

            const { bg_color, blockColor, blockColor2 } = style

            str += `
                [data-practice-unit-id='${unit.id}'] .shadow,
                [data-practice-unit-id='${unit.id}'] .practice-question__answer,
                [data-practice-unit-id='${unit.id}'] .conformity-item,
                [data-practice-unit-id='${unit.id}'] .order-answer,
                [data-practice-unit-id='${unit.id}'] .paste-word-container,
                [data-practice-unit-id='${unit.id}'] .conformity-item,
                [data-practice-unit-id='${unit.id}'] .default-dropdown__title,
                [data-practice-unit-id='${unit.id}'] .default-dropdown__current,
                [data-practice-unit-id='${unit.id}'] .default-input__input,
                [data-practice-unit-id='${unit.id}'] .practice-answer_scale,
                [data-practice-unit-id='${unit.id}'] .practice-question__statement {
                    background-color: ${blockColor !== "#FFFFFF" ? `${blockColor}` : "#FFFFFF"} !important;
                }

                [data-practice-unit-id='${unit.id}'] .shadow-container
                {
                    background-color: ${bg_color};
                }
                [data-practice-unit-id='${unit.id}'] .blockColor2
                {
                    background-color: ${blockColor2};
                }
                `

            return str
        },
        handleWheel(event) {
            if (this.displayModal) {
                event.preventDefault()
                event.stopPropagation()
                return false
            }
        }
    },
    watch: {
        error(val, oldVal) {
            if (val && !oldVal) {
                this.$notify({
                    position: "top-right",
                    color: "danger",
                    time: 5000,
                    title: this.t("expert.something_went_wrong"),
                    text: val?.response?.data?.message || val?.response?.data?.error || val,
                    icon: BugIcon
                })
            }
        }
    },
    computed: {
        proportionStyle() {
            return this._customStyle && this._customStyle.proportion
        },
        contentStyle() {
            let style = {}
            if (this.proportionStyle && this.contentPartScreen) {
                style = {
                    width: this.proportionStyle.right,
                    marginLeft: this.proportionStyle.right ? "15px" : "",
                    flex: this.proportionStyle.right ? "none" : ""
                }
            }

            const maxWidth = !this.contentPartScreen
                ? "100%"
                : this.$screenWidth > 1300
                ? "calc(100% - 510px)"
                : "calc(100% - 420px)"

            return { ...style, maxWidth }
        }
    }
}
</script>

<style scoped lang="sass">
.practice-view,.loader-block
    height: 100%
.practice-view-modal
    position: fixed
    height: 100vh
    width: 100vw
    z-index: 25
    top: 0
    left: 0
    backdrop-filter: blur(4px)
    &__overflow
        display: none
        position: absolute
        top: 0
        left: 0
        right: 0
        bottom: 0
        z-index: -1
        @media (max-width: 960px)
            display: block
    .practice-menu
        top: 50%
        transform: translateY(-50%)
        width: calc(100% - 64px)
        left: 32px
        max-height: calc(100vh - 180px)
        position: sticky
        @media (max-width: 960px)
            position: static
            transform: none
            margin: 70px auto 0
            max-height: 85vh
            height: auto
            min-height: 300px
        @media (max-width: 640px)
            left: 20px
            width: calc(100% - 40px)
        @media (max-width: 480px)
            left: 10px
            width: calc(100% - 20px)
.practice-view
    &__content
        height: 100%
        display: flex
        .practice-menu
            margin-right: 10px
</style>
